import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { defaultLandingPath } from '../utils/divisions';
import { safeLoginReturnPath } from '../utils/loginReturnPath';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_LABELS = { bronze: 'Bronze', silver: 'Silver', gold: 'Gold' };

export default function Login() {
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(searchParams.get('register') === 'true');
  const [selectedTier, setSelectedTier] = useState(searchParams.get('tier') || 'bronze');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        const newUser = await register(email, name, password, organizationName, selectedTier);
        toast.success('Account created successfully!');
        navigate(defaultLandingPath(newUser));
      } else {
        const loggedInUser = await login(email, password);
        toast.success('Welcome back!');
        navigate(safeLoginReturnPath(location.state?.returnTo) || defaultLandingPath(loggedInUser), { replace: true });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email: resetEmail });
      toast.success(response.data.message || 'If an account exists with that email, a password reset link has been sent.');
      setForgotPasswordOpen(false);
      setResetEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process request');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1676018367970-c11f6ad317ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtZWNoYW5pYyUyMHJlcGFpciUyMHNob3AlMjBpbmR1c3RyaWFsfGVufDB8fHx8MTc2OTk4NTc2Nnww&ixlib=rb-4.1.0&q=85"
          alt="Industrial repair shop"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900 bg-opacity-40"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-8">
            <img src="/logo.png" alt="ShopManager247" className="h-24 w-24 mx-auto rounded-2xl shadow-2xl mb-6" />
            <h1 className="text-4xl font-black tracking-tight mb-2">ShopManager<span className="text-orange-400">247</span></h1>
            <p className="text-lg font-medium text-blue-100">Professional Shop Management</p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h2 className="text-3xl font-black text-slate-900 mb-2">
              {isRegister ? 'Create Account' : 'Welcome Back'}
            </h2>
            <p className="text-slate-600">
              {isRegister ? 'Register your organization' : 'Sign in to your account'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {isRegister && (
              <>
                {/* Plan Tier Selector */}
                <div className="space-y-2">
                  <Label>Select Plan</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="tier-selector">
                    {['bronze', 'silver', 'gold'].map((tier) => (
                      <button
                        key={tier}
                        type="button"
                        onClick={() => setSelectedTier(tier)}
                        className={`p-3 rounded-lg border-2 text-center transition-all ${
                          selectedTier === tier
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`tier-btn-${tier}`}
                      >
                        <p className="text-sm font-bold text-slate-900">{TIER_LABELS[tier]}</p>
                        <p className="text-xs text-slate-500">
                          {tier === 'bronze' ? '$10/yr' : tier === 'silver' ? '$25/yr' : '$50/yr'}
                        </p>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-400 text-center">1-month free trial included</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="orgName">Company / Organization Name</Label>
                  <Input
                    id="orgName"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder="e.g., ABC Repair Services"
                    required
                    data-testid="org-name-input"
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">Your Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    data-testid="name-input"
                    className="h-12"
                  />
                </div>
              </>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="h-12"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-button"
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            >
              {loading ? 'Please wait...' : (isRegister ? 'Create Organization' : 'Sign In')}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-slate-600 hover:text-slate-900 block w-full"
              data-testid="toggle-register"
            >
              {isRegister ? 'Already have an account? Sign in' : 'Need to create an organization? Register'}
            </button>
            {!isRegister && (
              <button
                onClick={() => setForgotPasswordOpen(true)}
                className="text-sm text-blue-600 hover:text-blue-800"
                data-testid="forgot-password-btn"
              >
                Forgot your password?
              </button>
            )}
            <button
              onClick={() => navigate('/')}
              className="text-sm text-slate-400 hover:text-slate-600 block w-full"
              data-testid="back-to-home"
            >
              Back to home
            </button>
          </div>

          {/* Forgot Password Dialog */}
          <Dialog open={forgotPasswordOpen} onOpenChange={(open) => {
            setForgotPasswordOpen(open);
            if (!open) {
              setResetEmail('');
            }
          }}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Reset Password</DialogTitle>
                <DialogDescription>
                  Enter your account email. If it matches an account, we'll send you a password reset link.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="reset-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-slate-900"
                  data-testid="reset-send-email-btn"
                >
                  {resetLoading ? 'Sending…' : 'Send reset link'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}